// useIdentityPolling.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import RestHttpClient from '../../../common/RestHttpClient';

const POLL_INTERVAL = 10000;
const TERMINAL_STATUSES = ['accepted', 'completed', 'expired', 'cancelled', 'discarded'];
const SUCCESS_STATUSES = ['accepted', 'completed'];

export interface IdentityCheckData {
  checkId: string;
  checkDate: Date | undefined;
  url: string;
  expirationDate?: Date | undefined;
  status: string;
  details?: {
    bankAccount?: {
      accountHolder?: string;
      iban?: string;
      bic?: string;
      bankName?: string;
      bankCountry?: string;
    };
    nameCheckVerified?: boolean | null;
  };
}

export function useIdentityPolling(customerId: number | undefined, isOpen: boolean, dealId?: number) {
  const [data, setData] = useState<IdentityCheckData | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getIdentityCheck = async (customerId: number): Promise<IdentityCheckData | null> => {
    try {
      const result = await RestHttpClient.getIdentityCheckByCustomerId(customerId);
      if (result.data) {
        return {
          checkId: result.data.checkId || '',
          checkDate: result.data.checkDate,
          url: result.data.url || '',
          expirationDate: result.data.expirationDate,
          status: result.data.status || 'pending',
          details: {
            bankAccount: result.data.details?.bankAccount,
            nameCheckVerified: result.data.details?.nameCheckVerified,
          },
        };
      }
      return null;
    } catch (error) {
      // Return null if no identity check found (404) or other errors
      return null;
    }
  };

  const startIdentityCheck = async (customerId: number): Promise<string> => {
    const result = await RestHttpClient.startIdentityCheck(customerId, dealId);
    return result.data || '';
  };

  const stopPolling = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const poll = useCallback(async () => {
    if (!customerId) return;

    const result = await getIdentityCheck(customerId);
    const status = result?.status.toLowerCase() || '';

    if (result) setData(result);

    // Stop if we hit a final state or if the modal closed
    if (TERMINAL_STATUSES.includes(status) || !isOpen) {
      setLoading(false);
      return;
    }

    // Schedule next poll
    timerRef.current = setTimeout(poll, POLL_INTERVAL);
  }, [customerId, isOpen]);

  const start = useCallback(async () => {
    if (!customerId || !isOpen) return;

    setLoading(true);
    const existing = await getIdentityCheck(customerId);
    const status = existing?.status.toLowerCase() || '';

    // Strategy: If existing is valid, use it. If expired/none, start new.
    if (existing && !['expired', 'cancelled', 'discarded'].includes(status)) {
      setData(existing);
      if (!SUCCESS_STATUSES.includes(status)) {
        poll();
      } else {
        setLoading(false);
      }
    } else {
      const newId = await startIdentityCheck(customerId);
      setData((prev) => (prev ? { ...prev, checkId: newId, status: 'pending' } : null));
      poll();
    }
  }, [customerId, isOpen, poll]);

  useEffect(() => {
    if (isOpen) {
      start();
    } else {
      stopPolling();
      setData(null);
      setLoading(false);
    }
    return stopPolling;
  }, [isOpen, start]);

  return {
    data,
    loading,
    isPolling: !!timerRef.current && !TERMINAL_STATUSES.includes(data?.status.toLowerCase() || ''),
  };
}
